import { Popover } from '@ksyos/design-system';
import type { SortDirection } from '../../../../utils/sortDirection';
import type { IServiceOrganizationProvider } from '../../../interfaces/serviceOrganizationProvider';
import type { ITableHeader, ITableRow } from '../../../userDataManagement/SelectableSortableTable';
import { translateKey } from '../../../../translate';
import { SelectableSortableTable } from '../../../userDataManagement/SelectableSortableTable';

interface IPersonalSettingsPreferenceListReferralTableProps {
    isReadOnly: boolean;
    onChange: (selectedServiceOrganizationProviderOrganizationIds: string[]) => Promise<void>;
    onSort: (sortKey: string, sortDirection: SortDirection) => Promise<void>;
    selectedServiceOrganizationProviderOrganizationIds: string[];
    serviceOrganizationProviders: IServiceOrganizationProvider[];
    sortDirection: SortDirection;
    sortKey: string;
}

export function PersonalSettingsPreferenceListReferralTable(props: IPersonalSettingsPreferenceListReferralTableProps) {
    if (props.serviceOrganizationProviders.length === 0) {
        return null;
    }

    return (
        <SelectableSortableTable
            isReadOnly={props.isReadOnly}
            onSelect={async (selected) => props.onChange(selected)}
            onSort={props.onSort}
            selectedRowKeys={props.selectedServiceOrganizationProviderOrganizationIds}
            sortDirection={props.sortDirection}
            sortKey={props.sortKey}
            tableHeaders={getTableHeaders()}
            tableRows={getTableRows()}
        />
    );

    function getTableHeaders() {
        return [
            {
                columnKey: 'organization.name',
                children: translateKey('organization.organizationSpecialist'),
                isSortable: true,
            },
            { columnKey: 'organization.city', children: translateKey('common.city'), isSortable: true },
            { columnKey: 'applicationWaitingTime', children: translateKey('waitingTime.applicationWaitingTime') },
            { columnKey: 'treatmentWaitingTime', children: translateKey('waitingTime.treatmentWaitingTime') },
            {
                columnKey: 'organization.specialization',
                children: translateKey('personalSettings.preferenceList.specialization'),
            },
        ] as ITableHeader[];
    }

    function getTableRows() {
        return props.serviceOrganizationProviders.map((provider, index, array) => {
            const applicationWaitingTime = provider.organization.applicationWaitingTime;
            const treatmentWaitingTime = provider.organization.treatmentWaitingTime;

            return {
                rowKey: provider.organization.id,
                cells: [
                    { style: { fontWeight: 500 }, children: provider.organization.name },
                    { children: provider.organization.city },
                    {
                        children:
                            typeof applicationWaitingTime === 'number'
                                ? `${applicationWaitingTime} ${translateKey(
                                      applicationWaitingTime === 1 ? 'waitingTime.week' : 'waitingTime.weeks'
                                  )}`
                                : '-',
                    },
                    {
                        children:
                            typeof treatmentWaitingTime === 'number'
                                ? `${treatmentWaitingTime} ${translateKey(
                                      treatmentWaitingTime === 1 ? 'waitingTime.week' : 'waitingTime.weeks'
                                  )}`
                                : '-',
                    },
                    {
                        children:
                            provider.specializationDescriptions &&
                            provider.specializationDescriptions.join(', ').length > 30 ? (
                                <Popover content={provider.specializationDescriptions?.join(', ')}>
                                    {`${provider.specializationDescriptions.join(', ').substring(0, 30)}...`}
                                </Popover>
                            ) : (
                                provider.specializationDescriptions?.join(', ')
                            ),
                    },
                ],
            };
        }) as ITableRow[];
    }
}
